/**
 * Sparkline — one series, no axes, no legend.
 *
 * Chart decisions, and why:
 *   • One series, so there is no legend; the tile's own label names it.
 *   • The line is 2px and non-scaling, so stretching the box never thickens it.
 *   • Only the latest point is labelled. A number on every point is noise.
 *   • The line wears the primary hue. Status hues are reserved and appear only
 *     as a threshold band, when the caller sets one — never as "series 2".
 *   • Values and labels wear text tokens; the mark alone carries identity.
 *   • Hover is part of the chart, not an extra: moving across it reads out the
 *     nearest point, and the same readout is reachable from the keyboard.
 *   • The y-domain is padded and never auto-zoomed to the noise floor, so a
 *     99.1–99.4% week does not render as a dramatic mountain range.
 */
import { useId, useMemo, useState } from 'react';
import { cn } from '../lib/utils';

export interface SparkPoint {
  /** ISO timestamp or any label the tooltip should show. */
  label: string;
  value: number;
}

export interface SparklineProps {
  points: SparkPoint[];
  /** Accessible description of the series, e.g. "Availability, last 30 days". */
  title: string;
  /** Formats a value for the readout and the end label. */
  format?: (value: number) => string;
  /** Draw a band below this value in the warning hue (e.g. an SLA floor). */
  thresholdValue?: number;
  /** Fixed domain. Without it the domain is the data, padded. */
  domain?: { min: number; max: number };
  /** Rendered when there are fewer than two points. */
  emptyLabel?: string;
  className?: string;
}

const VIEW_W = 240;
const VIEW_H = 48;
const PAD_Y = 4;

export function Sparkline({
  points,
  title,
  format = (value) => String(Math.round(value)),
  thresholdValue,
  domain,
  emptyLabel,
  className,
}: SparklineProps) {
  const clipId = useId();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const geometry = useMemo(() => {
    if (points.length < 2) return null;
    const values = points.map((point) => point.value);
    const rawMin = domain?.min ?? Math.min(...values);
    const rawMax = domain?.max ?? Math.max(...values);
    // A flat series still needs a band to draw in.
    const span = rawMax - rawMin;
    const pad = domain ? 0 : span === 0 ? Math.max(1, Math.abs(rawMax) * 0.05) : span * 0.15;
    const min = rawMin - pad;
    const max = rawMax + pad;
    const range = max - min || 1;

    const x = (index: number) => (index / (points.length - 1)) * VIEW_W;
    const y = (value: number) => VIEW_H - PAD_Y - ((value - min) / range) * (VIEW_H - PAD_Y * 2);

    const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(2)},${y(point.value).toFixed(2)}`).join(' ');
    const area = `${line} L${VIEW_W},${VIEW_H} L0,${VIEW_H} Z`;
    const thresholdY = thresholdValue === undefined ? null : y(thresholdValue);

    return { x, y, line, area, thresholdY, min, max };
  }, [domain, points, thresholdValue]);

  if (!geometry) {
    return (
      <p className={cn('text-xs text-text-faint', className)}>
        {emptyLabel ?? 'Not enough data yet'}
      </p>
    );
  }

  const latest = points[points.length - 1];
  const active = activeIndex === null ? null : points[activeIndex];
  const readout = active ?? latest;

  return (
    <figure className={cn('m-0', className)}>
      <div className="relative">
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          className="h-12 w-full touch-none"
          role="img"
          aria-label={`${title}. ${format(latest.value)} latest.`}
          onPointerLeave={() => setActiveIndex(null)}
          onPointerMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            if (rect.width === 0) return;
            const ratio = (event.clientX - rect.left) / rect.width;
            const index = Math.round(ratio * (points.length - 1));
            setActiveIndex(Math.min(points.length - 1, Math.max(0, index)));
          }}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x="0" y="0" width={VIEW_W} height={VIEW_H} />
            </clipPath>
          </defs>

          {/* Threshold band: the only place a status hue appears. */}
          {geometry.thresholdY !== null && geometry.thresholdY < VIEW_H && (
            <rect
              x="0"
              y={geometry.thresholdY}
              width={VIEW_W}
              height={Math.max(0, VIEW_H - geometry.thresholdY)}
              fill="var(--warning)"
              opacity="0.10"
              clipPath={`url(#${clipId})`}
            />
          )}

          <path d={geometry.area} fill="var(--primary)" opacity="0.10" />
          <path
            d={geometry.line}
            fill="none"
            stroke="var(--primary)"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />

          {active && (
            <line
              x1={geometry.x(activeIndex!)}
              y1="0"
              x2={geometry.x(activeIndex!)}
              y2={VIEW_H}
              stroke="var(--text-faint)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}

        </svg>

        {/* The marker is an HTML element rather than an SVG circle: the viewBox
            is stretched with preserveAspectRatio="none", which turns a circle
            into an ellipse. Percentages put it exactly where the path point is. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-surface bg-primary"
          style={{
            left: `${(geometry.x(activeIndex ?? points.length - 1) / VIEW_W) * 100}%`,
            top: `${(geometry.y(readout.value) / VIEW_H) * 100}%`,
          }}
        />
      </div>

      <figcaption className="mt-1 flex items-baseline justify-between gap-2 text-[11px]">
        <span className="truncate text-text-faint">{readout.label}</span>
        <span className="font-medium text-text">{format(readout.value)}</span>
      </figcaption>
    </figure>
  );
}
